// src/utils/api.js
import axios from 'axios';
import { clearStoredAuth, getStoredToken } from './authStorage';

// Drops circular references and non-serializable values (DOM nodes, Files, functions)
// from a request body before axios's default JSON.stringify gets to it. Safari/WebKit
// throws "JSON.stringify cannot serialize cyclic structures." (a hard crash, not a
// catchable network error) if a stray ref or DOM node sneaks into a payload, so this
// runs on every JSON request rather than trying to track down each call site.
function decycle(value) {
  if (!value || typeof value !== 'object' || value instanceof FormData) return value;
  const seen = new WeakSet();
  const strip = (val) => {
    if (val === null || typeof val !== 'object') return val;
    if (typeof File !== 'undefined' && val instanceof File) return val;
    if (typeof Blob !== 'undefined' && val instanceof Blob) return val;
    if (typeof Node !== 'undefined' && val instanceof Node) return undefined;
    if (seen.has(val)) return undefined;
    seen.add(val);
    if (Array.isArray(val)) return val.map(strip);
    const out = {};
    for (const key of Object.keys(val)) {
      const stripped = strip(val[key]);
      if (stripped !== undefined) out[key] = stripped;
    }
    return out;
  };
  return strip(value);
}

// Smart API URL detection with multiple fallback options
const getApiUrl = () => {
  // Priority 1: Check URL parameter (e.g., ?api_url=http://192.168.1.100:5000/api)
  const urlParams = new URLSearchParams(window.location.search);
  const urlParamApiUrl = urlParams.get('api_url');
  if (urlParamApiUrl) {
    console.log('🌐 Using API URL from URL parameter:', urlParamApiUrl);
    localStorage.setItem('api_url', urlParamApiUrl); // Save for future use
    return urlParamApiUrl;
  }
  
  // Priority 2: Check localStorage (manual override)
  // But validate it's not an old IP from a different network
  const storedApiUrl = localStorage.getItem('api_url');
  if (storedApiUrl) {
    try {
      const currentHostname = window.location.hostname;
      const storedUrlObj = new URL(storedApiUrl);
      const storedHostname = storedUrlObj.hostname;
      
      // Get expected IP from environment variable
      const expectedIP = process.env.REACT_APP_API_IP;
      
      // If accessing via network IP but localStorage has localhost, ignore it
      if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1' && 
          (storedHostname === 'localhost' || storedHostname === '127.0.0.1')) {
        console.log('⚠️ Ignoring localStorage API URL (localhost) when accessing via network IP');
        localStorage.removeItem('api_url'); // Clear invalid cached URL
      } 
      // Check if stored IP differs from expected IP (from env) or current hostname
      else if (expectedIP && storedHostname !== expectedIP && storedHostname !== 'localhost' && storedHostname !== '127.0.0.1') {
        console.log(`⚠️ Network changed! Stored IP (${storedHostname}) differs from expected IP (${expectedIP}). Clearing cache.`);
        localStorage.removeItem('api_url'); // Clear old IP from different network
        // Don't use stored URL, let it fall through to auto-detection
      }
      // If accessing via network IP and stored URL has different IP, clear it and use current hostname
      else if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1' && 
               storedHostname !== currentHostname && storedHostname !== 'localhost' && storedHostname !== '127.0.0.1') {
        console.log(`⚠️ Network changed! Stored IP (${storedHostname}) differs from current IP (${currentHostname}). Clearing cache.`);
        localStorage.removeItem('api_url'); // Clear old IP from different network
        // Don't use stored URL, let it fall through to auto-detection
      } else {
        console.log('🌐 Using API URL from localStorage:', storedApiUrl);
        return storedApiUrl;
      }
    } catch (e) {
      console.warn('⚠️ Invalid stored API URL, clearing:', e);
      localStorage.removeItem('api_url');
    }
  }
  
  // Priority 3: Use full API URL environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Priority 4: Use IP address from environment variable
  if (process.env.REACT_APP_API_IP) {
    const protocol = 'http:';
    const port = process.env.REACT_APP_API_PORT || '5000';
    const apiUrl = `${protocol}//${process.env.REACT_APP_API_IP}:${port}/api`;
    console.log('🌐 Using API URL from REACT_APP_API_IP:', apiUrl);
    return apiUrl;
  }
  
  // Priority 5: Auto-detect from current hostname (works for both localhost and IP)
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const port = '5000';
  
  // If accessing from network IP, use that IP for API
  if (hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '') {
    const apiUrl = `${protocol}//${hostname}:${port}/api`;
    console.log('🌐 Auto-detected network API URL:', apiUrl);
    return apiUrl;
  }
  
  // Priority 6: Safe production fallback (desktop should not default to localhost).
  return 'https://restaurant-management-backend-yci5.onrender.com/api';
};

// Initialize API with detected URL
let apiBaseURL = getApiUrl();

const api = axios.create({
  baseURL: apiBaseURL,
  // Don't set default Content-Type - let it be set per request
  //
  // Global timeout: without one, a half-dead link (router up, WAN down —
  // navigator.onLine stays true so nothing routes to offline mode) leaves
  // requests hanging on OS-level TCP retries for minutes. Ayder 2026-07-15:
  // "Start Session" sat for 10-15 minutes with no feedback. 30s is generous
  // enough for large menu payloads on slow links; after that axios throws
  // ECONNABORTED, which offlineManager.isNetworkError treats as a network
  // loss and falls back to local. Callers needing longer (uploads) can
  // override per request.
  timeout: 30000
});

// Function to get current API URL (re-evaluates on each call)
api.getApiUrl = () => {
  return getApiUrl();
};

// Function to update API URL dynamically (useful for runtime changes)
api.updateBaseURL = (newUrl) => {
  api.defaults.baseURL = newUrl;
  apiBaseURL = newUrl;
  if (newUrl) {
    localStorage.setItem('api_url', newUrl);
  } else {
    localStorage.removeItem('api_url');
  }
  console.log('🔄 API Base URL updated to:', newUrl);
};

// Function to refresh API URL (re-detect and update)
api.refreshBaseURL = () => {
  const newUrl = getApiUrl();
  if (newUrl !== api.defaults.baseURL) {
    console.log('🔄 Refreshing API Base URL from', api.defaults.baseURL, 'to', newUrl);
    api.updateBaseURL(newUrl);
  }
  return newUrl;
};

// Force check and fix old IP on initialization
const expectedIP = process.env.REACT_APP_API_IP;
// Check for any old IP addresses that might be cached
const oldIPs = ['192.168.18.109', '192.168.50.24']; // Add any other old IPs here
const hasOldIP = oldIPs.some(oldIP => api.defaults.baseURL.includes(oldIP) && oldIP !== expectedIP);

if (expectedIP && hasOldIP) {
  console.log('⚠️ Detected old/cached IP on initialization, fixing...');
  console.log('⚠️ Current baseURL:', api.defaults.baseURL);
  console.log('⚠️ Expected IP:', expectedIP);
  localStorage.removeItem('api_url');
  const newUrl = getApiUrl();
  api.defaults.baseURL = newUrl;
  apiBaseURL = newUrl;
  console.log('✅ Fixed API URL to:', newUrl);
}

// Also check localStorage for old IPs
try {
  const storedUrl = localStorage.getItem('api_url');
  if (storedUrl) {
    const hasStoredOldIP = oldIPs.some(oldIP => storedUrl.includes(oldIP) && oldIP !== expectedIP);
    if (hasStoredOldIP && expectedIP) {
      console.log('⚠️ Found old IP in localStorage, clearing...');
      localStorage.removeItem('api_url');
      const newUrl = getApiUrl();
      api.defaults.baseURL = newUrl;
      apiBaseURL = newUrl;
      console.log('✅ Updated API URL to:', newUrl);
    }
  }
} catch (e) {
  console.warn('Error checking localStorage:', e);
}

// Debug: Log the API base URL
console.log('🔧 API Base URL:', api.defaults.baseURL);
console.log('🔧 REACT_APP_API_URL env:', process.env.REACT_APP_API_URL);
console.log('🔧 REACT_APP_API_IP env:', process.env.REACT_APP_API_IP);
console.log('🔧 Current hostname:', window.location.hostname);
console.log('💡 To change API URL, use: localStorage.setItem("api_url", "http://YOUR_IP:5000/api")');
console.log('💡 To refresh API URL, use: api.refreshBaseURL()');

// Add a request interceptor
api.interceptors.request.use(
  async (config) => {
    // Refresh API URL on each request to handle network changes
    // This ensures we always use the correct IP if network changed
    const currentApiUrl = getApiUrl();
    if (currentApiUrl !== api.defaults.baseURL) {
      console.log('🔄 Detected API URL change, updating from', api.defaults.baseURL, 'to', currentApiUrl);
      api.defaults.baseURL = currentApiUrl;
      apiBaseURL = currentApiUrl;
    }
    
    // Force check: if current baseURL contains old IP, clear localStorage and refresh
    const expectedIP = process.env.REACT_APP_API_IP;
    if (expectedIP && api.defaults.baseURL.includes('192.168.18.109')) {
      console.log('⚠️ Detected old IP in baseURL, clearing cache and updating...');
      localStorage.removeItem('api_url');
      const newUrl = getApiUrl();
      api.defaults.baseURL = newUrl;
      apiBaseURL = newUrl;
      config.baseURL = newUrl;
      console.log('✅ Updated API URL to:', newUrl);
    }
    
    const token = await getStoredToken();
    if (token) {
      config.headers['x-auth-token'] = token;
    }

    if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
      config.headers['X-Resto360-Client'] = 'electron';
    }

    // Set Content-Type for non-FormData requests
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
      config.data = decycle(config.data);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Prevents multiple simultaneous 401 responses from each independently calling
// clearStoredAuth() and window.location.href — which causes a cascade of redundant
// storage clears and redirect attempts before the page actually navigates away.
let is401Redirecting = false;

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Any successful response proves we're online — let the offline manager
    // restore immediately (no-op if already online). Stops the yellow offline
    // bar from sticking after a brief blip until the slow recovery probe.
    if (typeof api.onResponseSuccess === 'function') {
      try { api.onResponseSuccess(); } catch (_) {}
    }
    return response;
  },
  async (error) => {
    // Handle blocked accounts / suspended branches (403 with a known code)
    if (error.response?.status === 403 &&
        (error.response?.data?.code === 'ACCOUNT_BLOCKED' || error.response?.data?.code === 'BRANCH_SUSPENDED')) {
      await clearStoredAuth();
      const blockedPath = window.electronAPI?.isElectron ? '/#/blocked' : '/blocked';
      window.location.href = blockedPath;
      return Promise.reject(error);
    }

    // Handle unauthorized errors (401)
    if (error.response && error.response.status === 401) {
      // Do NOT log out when offline — the token is still valid, the server is
      // just unreachable.  Only clear auth when we actually get a 401 from the
      // server while online.
      if (navigator.onLine !== false && !is401Redirecting) {
        is401Redirecting = true;
        await clearStoredAuth();
        const signInPath = window.electronAPI?.isElectron ? '/#/signin' : '/signin';
        window.location.href = signInPath;
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Helper function to get full image URL from relative path
export const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return null;
  const path = imagePath.trim();
  if (!path) return null;

  if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const apiUrl = getApiUrl();
  const baseUrl = apiUrl.replace(/\/api\/?$/, '');

  if (path.startsWith('/uploads')) {
    return `${baseUrl}${path}`;
  }
  if (path.startsWith('uploads')) {
    return `${baseUrl}/${path}`;
  }
  return `${baseUrl}/uploads/${path}`;
};

export default api;